import React from 'react';
import { isRequestCancelled } from './requestStatus';

// The request stage bar: رفع الطلب → قبول العرض → الدفعة الأولى → الإنتاج →
// الشحن → الاستلام.
//
// ── Why this file exists ────────────────────────────────────────────────────
// It was three things at the top of DashboardBuyer.jsx — TIMELINE_STEPS,
// timelineIndexFromStatus and TimelineBar — none of them exported. The admin
// console needed the same bar, and the only ways to get it were to import from
// a page component or to write a third copy. A third copy is how the SECOND
// copy came about, and that one has already drifted (see the warning below).
//
// ── THE TRADER'S BAR IS THE ONE THAT WON, and that was a decision ──────────
// Owner, 2026-09-22. This module is the trader's definition, moved verbatim.
// The step list, the status→index map and the rendering are unchanged from
// what DashboardBuyer shipped, so no trader sees anything different today
// except on a cancelled request (below).
//
// ⚠️ THE SUPPLIER HAS A SECOND, DIFFERENT BAR AND IT IS STILL THERE.
// DashboardSupplier.jsx:2775 has SUPPLIER_STEPS_AR/EN/ZH + SUPPLIER_STEP_MAP +
// SupplierStatusTimeline. It is NOT this bar with different wording — it is a
// different bar:
//   · it has a step this one does not («تأكيد الجاهزية») and lacks one this
//     one has («الدفعة الأولى»), so from the third step onward THE SAME
//     requests.status LIGHTS A DIFFERENT POSITION on each page;
//   · supplier_confirmed is step 1 here and step 2 there;
//     paid is 2 here and 3 there; delivered is 5 here and 6 there.
// Unifying them changes a supplier-facing screen, which is its own decision
// and its own visual check. Logged in docs/TECH_DEBT.md and
// docs/NEXT_SESSION_PLAN.md. DO NOT quietly point the supplier at this module
// as a tidy-up: the steps are not interchangeable.

/**
 * The six stages, in order.
 *
 * zh was ADDED here (owner, 2026-09-22). The original pair was consulted as
 * `isAr ? step.ar : step.en`, so a Chinese reader got English on a bar whose
 * neighbouring supplier version has had zh all along. The words follow the
 * vocabulary already used elsewhere in the product rather than being invented:
 * 生产中 and 运输中 are the supplier bar's and requestStatus.js's words for the
 * same two stages.
 */
export const REQUEST_TIMELINE_STEPS = [
  { key: 'posted',    ar: 'رفع الطلب',     en: 'Posted',      zh: '提交需求' },
  { key: 'accepted',  ar: 'قبول العرض',    en: 'Accepted',    zh: '接受报价' },
  { key: 'paid',      ar: 'الدفعة الأولى', en: '1st Payment', zh: '首付款'   },
  { key: 'producing', ar: 'الإنتاج',       en: 'Production',  zh: '生产中'   },
  { key: 'shipping',  ar: 'الشحن',         en: 'Shipping',    zh: '运输中'   },
  { key: 'received',  ar: 'الاستلام',      en: 'Received',    zh: '已收货'   },
];

/**
 * Which stage a status is at.
 *
 * Moved verbatim from DashboardBuyer. Note what it does NOT list: `cancelled`
 * has no entry, and neither do supplier_rejected or
 * pending_supplier_confirmation. They fall to `?? 0` — stage one, "posted" —
 * which is correct for the two RFQ states and WRONG for cancelled, because a
 * cancelled request is not at the start of its life, it is out of it. That is
 * handled by the component rather than by inventing a seventh stage; see
 * RequestTimeline.
 */
export function requestTimelineIndex(status) {
  const map = {
    open: 0, offers_received: 0,
    closed: 1, supplier_confirmed: 1,
    paid: 2,
    ready_to_ship: 3,
    shipping: 4,
    arrived: 5, delivered: 5,
  };
  return map[status] ?? 0;
}

/**
 * A step list + the active index.
 *
 * ⚠️ Its old comment claimed it was "reused by both the direct-order lifecycle
 * and the managed/factory sourcing timeline". It had exactly ONE caller when
 * this was extracted; the managed lifecycle moved to /managed-order/:id and
 * nobody came back to the comment. Corrected rather than carried over, because
 * a comment describing a second caller that does not exist is the kind that
 * gets trusted (golden rule 11).
 *
 * `dimmed` greys the whole bar and suppresses the active marker. Colours are
 * the same values the bar already used; nothing new is introduced.
 */
export function TimelineBar({ steps, current, lang = 'ar', dimmed = false }) {
  const isAr = lang === 'ar';
  const DONE = dimmed ? 'var(--border-default)' : 'rgba(45,122,79,0.7)';
  const DONE_TEXT = dimmed ? 'var(--text-disabled)' : 'rgba(45,122,79,0.9)';
  const ACTIVE_TEXT = dimmed ? 'var(--text-disabled)' : 'var(--text-primary)';
  return (
    <div style={{ overflowX: 'auto', margin: '12px 0 4px', paddingBottom: 2, opacity: dimmed ? 0.45 : 1 }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', minWidth: 360, position: 'relative' }}>
        {/* connector track */}
        <div style={{ position: 'absolute', top: 7, left: 7, right: 7, height: 1, background: 'var(--border-subtle)', zIndex: 0 }} />
        <div style={{ position: 'absolute', top: 7, left: 7, height: 1, zIndex: 0, background: dimmed ? 'var(--border-default)' : 'rgba(45,122,79,0.55)', width: `calc(${(current / (steps.length - 1)) * 100}% - 14px)`, transition: 'width 0.4s ease' }} />
        {steps.map((step, i) => {
          const done   = i < current;
          const active = i === current && !dimmed;
          return (
            <div key={step.key} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', position: 'relative', zIndex: 1 }}>
              <div style={{
                width: 14, height: 14, borderRadius: '50%', marginBottom: 6, flexShrink: 0,
                background: done ? DONE : active ? 'var(--text-primary)' : 'var(--bg-raised)',
                border: active ? '2px solid var(--text-primary)' : done ? `2px solid ${DONE}` : '2px solid var(--border-default)',
                boxShadow: 'none',
                transition: 'all 0.3s',
              }} />
              <p style={{
                fontSize: 9, lineHeight: 1.3, textAlign: 'center',
                color: done ? DONE_TEXT : active ? ACTIVE_TEXT : 'var(--text-disabled)',
                fontWeight: active ? 600 : 400,
                fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)',
                whiteSpace: 'nowrap',
              }}>
                {step[lang] || step.en}
              </p>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/**
 * The stage bar for one request.
 *
 * ── Cancelled requests are DIMMED, not hidden ──────────────────────────────
 * Folded in here from docs/NEXT_SESSION_PLAN.md item 1 (logged 2026-09-20),
 * which is the same bug on the same component: requestTimelineIndex has no
 * entry for `cancelled`, so it returned 0 and the bar announced «رفع الطلب» as
 * the ACTIVE stage on a request the trader had already killed — a dead request
 * presented as one at the start of its life.
 *
 * Dimmed rather than hidden because the stages are still the answer to "how far
 * did this get before it stopped", which is exactly what someone looking at a
 * cancelled request wants. Hiding it would replace a wrong answer with no
 * answer.
 *
 * This is the ONE behavioural difference between this module and the code it
 * was extracted from, and it applies to the trader's page as well as the
 * admin's — which is what item 1 asked for.
 */
export function RequestTimeline({ request, lang = 'ar' }) {
  // ⚠️ TWO COLUMNS, AND WHICH ONE ANSWERS WHICH QUESTION IS NOT OBVIOUS.
  //
  // The STAGE comes from `shipping_status || status` — that is what
  // DashboardBuyer passed, and shipping_status is what advances once a
  // shipment exists. But CANCELLATION lives only on `status`, so a cancelled
  // request that had already shipped would read its stage from
  // shipping_status and look perfectly alive.
  //
  // Both reads happen HERE, once, rather than at each call site. A call site
  // that had to remember to pass both is a call site that will pass one — the
  // same failure shape as DistributePanel's `offerable`/`rawStatus` props.
  const stageStatus = request?.shipping_status || request?.status;
  return (
    <TimelineBar
      steps={REQUEST_TIMELINE_STEPS}
      current={requestTimelineIndex(stageStatus)}
      lang={lang}
      dimmed={isRequestCancelled(request?.status)}
    />
  );
}
